import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useNotificationPreferences, useUpdateNotificationPreferences } from "@/hooks/api/useNotificationPreferences";
import { NotificationPreferences } from "@/types/api";
import { Bell, RefreshCw, UserPlus, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

type NotificationPreferenceKey = 
  | "enabled"
  | "suggestion_emails"
  | "followup_emails"
  | "inactive_reminder_emails"
  | "new_user_nudge_emails";

interface NotificationType {
  key: NotificationPreferenceKey;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const notificationTypes: NotificationType[] = [
  {
    key: "suggestion_emails",
    label: "Suggestion Emails",
    description: "Receive personalized suggestions for staying connected",
    icon: <Bell className="w-4 h-4" />,
  },
  {
    key: "followup_emails",
    label: "Follow-up Emails",
    description: "Get reminders to follow up on suggestions",
    icon: <RefreshCw className="w-4 h-4" />,
  },
  {
    key: "inactive_reminder_emails",
    label: "Inactive Reminder Emails",
    description: "Receive reminders when you haven't logged in for a while",
    icon: <Clock className="w-4 h-4" />,
  },
  {
    key: "new_user_nudge_emails",
    label: "New User Nudge Emails",
    description: "Get helpful tips and nudges as a new user",
    icon: <UserPlus className="w-4 h-4" />,
  },
];

export const NotificationSettings = () => {
  const { data: preferences, isLoading, error } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const [localPreferences, setLocalPreferences] = useState<Record<string, boolean>>({});

  // Initialize local state when preferences are loaded
  useEffect(() => {
    if (preferences) {
      setLocalPreferences({
        enabled: preferences.enabled,
        suggestion_emails: preferences.suggestion_emails,
        followup_emails: preferences.followup_emails,
        inactive_reminder_emails: preferences.inactive_reminder_emails,
        new_user_nudge_emails: preferences.new_user_nudge_emails,
      });
    }
  }, [preferences]);

  const handleToggle = async (key: string, value: boolean) => {
    const newPreferences = { ...localPreferences, [key]: value };
    
    // If master toggle is turned off, disable all individual toggles
    if (key === "enabled" && !value) {
      Object.keys(newPreferences).forEach((k) => {
        if (k !== "enabled") {
          newPreferences[k] = false;
        }
      });
    }
    
    // If master toggle is turned on, keep individual toggles as they were
    // (they'll be restored from the previous state)
    if (key === "enabled" && value && preferences) {
      newPreferences.suggestion_emails = preferences.suggestion_emails;
      newPreferences.followup_emails = preferences.followup_emails;
      newPreferences.inactive_reminder_emails = preferences.inactive_reminder_emails;
      newPreferences.new_user_nudge_emails = preferences.new_user_nudge_emails;
    }

    setLocalPreferences(newPreferences);

    // Update backend
    try {
      await updatePreferences.mutateAsync(newPreferences as any);
    } catch (error) {
      // Revert on error
      if (preferences) {
        setLocalPreferences({
          enabled: preferences.enabled,
          suggestion_emails: preferences.suggestion_emails,
          followup_emails: preferences.followup_emails,
          inactive_reminder_emails: preferences.inactive_reminder_emails,
          new_user_nudge_emails: preferences.new_user_nudge_emails,
        });
      }
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-[20px] font-bold text-wabisabi-plum">
            Notification Settings
          </CardTitle>
          <CardDescription>
            Manage your email notification preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-[20px] font-bold text-wabisabi-plum">
            Notification Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600">
            Failed to load notification preferences. Please try again later.
          </p>
        </CardContent>
      </Card>
    );
  }

  const masterEnabled = localPreferences.enabled ?? preferences?.enabled ?? true;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[20px] font-bold text-wabisabi-plum">
          Notification Settings
        </CardTitle>
        <CardDescription>
          Manage your email notification preferences
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Master Toggle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="text-[#6C4F77]">
                  <Bell className="w-5 h-5" />
                </div>
                <Label htmlFor="master-toggle" className="text-[14px] font-medium text-black cursor-pointer">
                  Email Notifications
                </Label>
              </div>
              <Switch
                id="master-toggle"
                checked={masterEnabled}
                onCheckedChange={(checked) => handleToggle("enabled", checked)}
                disabled={updatePreferences.isPending}
                className="data-[state=checked]:bg-[#6C4F77]"
              />
            </div>
            <p className="text-[12px] text-[#8E8E8E] pl-7">
              Master switch for all email notifications. When disabled, all email notifications are turned off.
            </p>
          </div>

          <Separator />

          {/* Individual Notification Types */}
          <div className="space-y-4">
            {notificationTypes.map((type) => {
              const isEnabled = localPreferences[type.key] ?? preferences?.[type.key] ?? false;
              const isDisabled = !masterEnabled || updatePreferences.isPending;

              return (
                <div key={type.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`${isDisabled ? "text-gray-400" : "text-[#6C4F77]"}`}>
                        {type.icon}
                      </div>
                      <Label
                        htmlFor={type.key}
                        className={`text-[14px] font-medium ${
                          isDisabled ? "text-gray-400 cursor-not-allowed" : "text-black cursor-pointer"
                        }`}
                      >
                        {type.label}
                      </Label>
                    </div>
                    <Switch
                      id={type.key}
                      checked={isEnabled && masterEnabled}
                      onCheckedChange={(checked) => handleToggle(type.key, checked)}
                      disabled={isDisabled}
                      className="data-[state=checked]:bg-[#6C4F77]"
                    />
                  </div>
                  <p className={`text-[12px] pl-6 ${isDisabled ? "text-gray-400" : "text-[#8E8E8E]"}`}>
                    {type.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

